import { Pipe, PipeTransform, TrackByFunction } from '@angular/core';


/**
 * Pipe to reduce boiler-plate for the most commonly-needed trackBy functions. If a property name (or array of property
 * names) is specified, the items are tracked by specified propert(y|ies).
 *
 * Otherwise, the pipe recognizes the following "pseudo" properties:
 *  * $index: items are tracked by their index in the array
 *  * $identity: items are tracked by their own value -- should be used for primitives (string, number, boolean).
 *               This is Angular's default behavior when no "trackBy" function is specified, but this usage will satisfy
 *               the tslint "template-use-track-by-function" rule.
 *
 *  @example
 *  ```
 *  *ngFor="let item of items; trackBy: ('$index' | trackByProperty)"
 *  *ngFor="let item of items; trackBy: ('$identity' | trackByProperty)"
 *  *ngFor="let item of items; trackBy: ('id' | trackByProperty)"
 *  *ngFor="let item of items; trackBy: (['type', 'name'] | trackByProperty)"
 *  ```
 */
@Pipe({
    name: 'trackByProperty',
    standalone: true,
    pure: true
})
export class TrackByPropertyPipe implements PipeTransform {
    // Unfortunately Angular can't infer generic types for pipes from templates, so we need to use "any" for now
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transform(propertyNames: '$identity' | '$index' | string | string[]): TrackByFunction<any>;

    // `_.memoize` doesn't support arrays as a cache key, so we need to construct a key from the property names and use

    transform<T>(propertyNames: (keyof T)[] | keyof T): TrackByFunction<T> {
        if (Array.isArray(propertyNames)) {
            return this.getTrackByMultiPropertyFn<T>(propertyNames.join('|'), propertyNames);
        } else if (propertyNames === '$index') {
            return this.trackByIndex;
        } else if (propertyNames === '$identity') {
            return this.trackByIdentity as TrackByFunction<T>;
        } else if (typeof propertyNames === 'string') {
            return this.getTrackByPropertyFn<T>(propertyNames as keyof T);
        }

        throw new Error('Invalid argument for TrackByPropertyPipe');
    }

    private readonly trackByProperty =
        // eslint-disable-next-line @typescript-eslint/naming-convention
        <T>(propertyName: keyof T): TrackByFunction<T> => (_index, item) => item[propertyName];

    // Memoize the property functions to avoid a large number of function instances being constructed at runtime

    private readonly getTrackByPropertyFn: <T>(propertyName: keyof T) => TrackByFunction<T> = this.memoize(<T>(prop: keyof T) =>
        this.trackByProperty<T>(prop)
    );

    // eslint-disable-next-line @typescript-eslint/naming-convention
    private readonly trackByMultiProperty = <T>(_propertyNamesKey: string, propertyNames: (keyof T)[]): TrackByFunction<T> => (_index, item) => propertyNames.map((p) => item[p]);

    // it as the first argument (which `_.memoize` uses as the cache key)

    private readonly getTrackByMultiPropertyFn: <T>(propertyNamesKey: string, propertyNames: (keyof T)[]) => TrackByFunction<T> =
        this.memoize(<T>(key: string, propNames: (keyof T)[]) => this.trackByMultiProperty<T>(key, propNames));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars,@typescript-eslint/naming-convention
    private readonly trackByIndex: TrackByFunction<any> = (index: number, _item: any) => index;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/naming-convention
    private readonly trackByIdentity = (_index: number, item: any) => item;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    memoize(func) {
        const cache = new Map();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (...args: any) => {
            const key = JSON.stringify(args); // Create a unique key for the cache

            if (cache.has(key)) {

                return cache.get(key); // Return cached result if exists
            }


            const result = func.apply(this, args); // Call the original function
            cache.set(key, result); // Store the result in the cache


            return result;
        };
    }
}
